<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ListingDiscoveryCard from './ListingDiscoveryCard.vue';

const props = defineProps({
  cards: { type: Array, default: () => [] },
  activeId: { type: [String, Number], default: '' },
});

const emit = defineEmits([
  'open',
  'hover',
  'toggle-select',
  'public-link',
  'share',
  'share-error',
  'viewed',
  'activeChange',
  'alertClick',
]);

const { t } = useI18n();
const scrollEl = ref(null);
const touchStartX = ref(null);

const activeIndex = computed(() => {
  const index = props.cards.findIndex(
    card => String(card.id) === String(props.activeId)
  );
  return index >= 0 ? index : 0;
});

const activeCard = computed(() => props.cards[activeIndex.value]);
const canNavigate = computed(() => props.cards.length > 1);
const positionLabel = computed(() =>
  t('PROPERTIES.LIST.MAP.POSITION', {
    current: props.cards.length ? activeIndex.value + 1 : 0,
    total: props.cards.length,
  })
);

const goTo = index => {
  if (!props.cards.length) return;
  const nextIndex = (index + props.cards.length) % props.cards.length;
  const card = props.cards[nextIndex];
  emit('activeChange', card);
};

const handleKeydown = event => {
  if (event.key === 'ArrowLeft') {
    event.preventDefault();
    goTo(activeIndex.value - 1);
  } else if (event.key === 'ArrowRight') {
    event.preventDefault();
    goTo(activeIndex.value + 1);
  }
};

const handleTouchStart = event => {
  touchStartX.value = event.touches?.[0]?.clientX ?? null;
};

const handleTouchEnd = event => {
  if (touchStartX.value == null) return;
  const endX = event.changedTouches?.[0]?.clientX ?? touchStartX.value;
  const delta = endX - touchStartX.value;
  touchStartX.value = null;
  if (Math.abs(delta) < 36) return;
  goTo(delta > 0 ? activeIndex.value - 1 : activeIndex.value + 1);
};

watch(
  () => props.activeId,
  () => {
    const element = scrollEl.value?.children?.[activeIndex.value];
    element?.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest',
    });
  }
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section
    class="rounded-lais border border-lais-border-soft bg-white p-3 shadow-lais-card"
    tabindex="0"
    :aria-label="t('PROPERTIES.LIST.MAP.CAROUSEL')"
    @keydown="handleKeydown"
    @touchstart.passive="handleTouchStart"
    @touchend.passive="handleTouchEnd"
  >
    <div class="mb-3 flex items-center justify-between gap-3">
      <div class="min-w-0">
        <p class="text-[12px] font-semibold uppercase text-lais-text-muted">
          {{ t('PROPERTIES.LIST.MAP.CAROUSEL') }}
        </p>
        <p class="truncate text-[13px] text-lais-text">
          {{ activeCard?.title || t('PROPERTIES.LIST.MAP.SELECT_PROPERTY') }}
        </p>
      </div>
      <div class="flex items-center gap-2">
        <span
          class="rounded-full bg-lais-primary-50 px-2.5 py-1 text-[11px] font-semibold text-lais-primary"
        >
          {{ positionLabel }}
        </span>
        <button
          type="button"
          class="grid size-8 place-items-center rounded-lais border border-lais-border bg-white text-lais-text disabled:opacity-40"
          :disabled="!canNavigate"
          :aria-label="t('PROPERTIES.LIST.MAP.PREVIOUS')"
          @click="goTo(activeIndex - 1)"
        >
          <span class="i-lucide-chevron-left size-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          class="grid size-8 place-items-center rounded-lais border border-lais-border bg-white text-lais-text disabled:opacity-40"
          :disabled="!canNavigate"
          :aria-label="t('PROPERTIES.LIST.MAP.NEXT')"
          @click="goTo(activeIndex + 1)"
        >
          <span class="i-lucide-chevron-right size-4" aria-hidden="true" />
        </button>
      </div>
    </div>

    <div ref="scrollEl" class="flex gap-3 overflow-x-auto pb-1" role="list">
      <div
        v-for="card in cards"
        :key="card.id"
        class="w-[280px] shrink-0"
        role="listitem"
      >
        <ListingDiscoveryCard
          :card="card"
          :active="String(card.id) === String(activeId)"
          compact
          @open="payload => emit('open', payload)"
          @hover="payload => emit('hover', payload)"
          @toggle-select="payload => emit('toggle-select', payload)"
          @public-link="payload => emit('public-link', payload)"
          @share="
            (cardPayload, sharePayload) =>
              emit('share', cardPayload, sharePayload)
          "
          @share-error="
            (cardPayload, error) => emit('share-error', cardPayload, error)
          "
          @viewed="payload => emit('viewed', payload)"
        />
      </div>
      <button
        type="button"
        class="flex w-[240px] shrink-0 flex-col justify-center rounded-lais border border-dashed border-lais-border-soft bg-lais-bg-muted p-4 text-left text-[13px] text-lais-text-muted transition-colors hover:border-lais-primary hover:bg-lais-primary-50 hover:text-lais-primary"
        :aria-label="t('PROPERTIES.LIST.OPEN_ALERTS')"
        @click="emit('alertClick')"
      >
        <span class="i-lucide-bell-plus mb-2 size-5" aria-hidden="true" />
        {{ t('PROPERTIES.LIST.MAP.ALERT_HINT') }}
      </button>
    </div>
  </section>
</template>
